import React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from './ui/sidebar';
import { cn } from '../../lib/utils';
import styles from './NavDocuments.module.scss';
import sidebarStyles from './ui/sidebar.module.scss';

interface BaseMenuItem {
  title: string;
  label?: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface RegularMenuItem extends BaseMenuItem {
  type: 'item';
  url: string;
  label?: string;
  activePrefixes?: string[];
}

interface CollapsibleMenuItem extends BaseMenuItem {
  label?: string;
  type: 'group';
  items: {
    title: string;
    url: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

type MenuItem = RegularMenuItem | CollapsibleMenuItem;

interface NavDocumentsProps {
  items: MenuItem[];
}

export function NavDocuments({ items }: NavDocumentsProps) {
  const { state, setOpen } = useSidebar();
  const location = useLocation();

  // Tính toán group cha active dựa trên route hiện tại
  const getActiveGroups = (pathname: string): Record<string, boolean> => {
    const groups: Record<string, boolean> = {};
    for (const item of items) {
      if (item.type === 'group') {
        const hasActiveChild = item.items.some(
          (sub) => pathname === sub.url || pathname.startsWith(sub.url + '/')
        );
        if (hasActiveChild) {
          groups[item.title] = true;
        }
      }
    }
    return groups;
  };

  // Khởi tạo state với active groups ngay lập tức (không chờ useEffect)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () => getActiveGroups(location.pathname)
  );

  // Track pathname trước đó để chỉ merge khi route thay đổi
  const prevPathname = useRef(location.pathname);

  // Khi route thay đổi, merge active group vào state hiện tại (không reset)
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      prevPathname.current = location.pathname;
      if (state !== 'collapsed') {
        const activeGroups = getActiveGroups(location.pathname);
        setExpandedGroups(prev => ({
          ...prev,
          ...activeGroups,
        }));
      }
    }
  }, [location.pathname, state]);

  useEffect(() => {
    if (state === 'collapsed') {
      setExpandedGroups({});
    } else {
      // Khi expand lại sidebar, restore active groups
      setExpandedGroups(prev => ({
        ...prev,
        ...getActiveGroups(location.pathname),
      }));
    }
  }, [state]);

  const toggleGroup = (title: string) => {
    // If sidebar is collapsed, expand it first
    if (state === 'collapsed') {
      setOpen(true);
    }

    // Then toggle the group
    setExpandedGroups(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  return (
    <SidebarGroup className="sidebarGroupP0">
      <SidebarMenu>
        {items.map((item, index) => {
          if (item.type === 'item') {
            const Icon = item.icon;

            return (
              <React.Fragment key={`${item.title}-${index}`}>
                {item.label && state === "expanded" && (
                  <div className="groupLabel">
                    {item.label}
                  </div>
                )}

                <SidebarMenuItem>
                  <NavLink
                    to={item.url}
                    style={{ textDecoration: 'none' }}
                    end
                  >
                    {({ isActive }) => {
                      const extraActive = item.activePrefixes?.some(prefix =>
                        location.pathname.startsWith(prefix)
                      ) ?? false;
                      const active = isActive || extraActive;
                      return (
                        <SidebarMenuButton
                          isActive={active}
                          tooltip={state === 'collapsed' ? item.title : undefined}
                          className={cn(
                            "p-1em-center",
                            styles.navLink,
                            active && styles.navLinkActive
                          )}
                        >
                          <Icon className={styles.navIcon} />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      );
                    }}
                  </NavLink>
                </SidebarMenuItem>
              </React.Fragment>
            );
          }

          if (item.type === 'group') {
            const Icon = item.icon;
            const isExpanded = expandedGroups[item.title] || false;

            return (
              <>
                {item.label && state === "expanded" && (
                  <div className="groupLabel">
                    {item.label}
                  </div>
                )}

                <SidebarMenuItem key={`${item.title}-${index}`}>
                  <SidebarMenuButton
                    onClick={() => toggleGroup(item.title)}
                    tooltip={state === 'collapsed' ? item.title : undefined}
                    className={cn(styles.navLink, "p-1em-center")}
                  >
                    <Icon className={styles.navIcon} />
                    <span>{item.title}</span>
                    <ChevronRight
                      className={cn(
                        styles.chevron,
                        isExpanded && styles.chevronExpanded
                      )}
                    />
                  </SidebarMenuButton>

                  <div className={cn(
                    sidebarStyles.sidebarMenuSubWrapper,
                    isExpanded && sidebarStyles.sidebarMenuSubExpanded
                  )}>
                    <div className={sidebarStyles.sidebarMenuSubInner}>
                      <SidebarMenuSub>
                        {item.items.map((subItem, subIndex) => {
                          const SubIcon = subItem.icon;
                          return (
                            <SidebarMenuSubItem key={`${subItem.title}-${subIndex}`}>
                              <NavLink to={subItem.url} end style={{ textDecoration: 'none' }}>
                                {({ isActive }) => (
                                  <SidebarMenuSubButton
                                    isActive={isActive}
                                    className={cn(
                                      styles.navSubLink,
                                      isActive && styles.navSubLinkActive
                                    )}
                                  >
                                    {/* <SubIcon className={styles.navIcon} /> */}
                                    <span>{subItem.title}</span>
                                  </SidebarMenuSubButton>
                                )}
                              </NavLink>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </div>
                  </div>
                </SidebarMenuItem>
              </>
            );
          }

          return null;
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
